import styled, { css, keyframes } from 'styled-components'

import { _XL_Medium, _XS_Medium, mainColors } from '../../styles'
import { Button } from '../Button'

const animPulse = keyframes`
  from {
    transform: scale(0.98);
  }
  to {
    transform: scale(0.65);
  }
`

export const Root = styled.div`
  --month-gap: var(--year-calendar-month-gap, 8px);
  --day-size: var(--year-calendar-day-size, 12px);
  --week-summary-width: 16px;

  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 8px;
`

export const CalendarTitle = styled.div`
  color: ${mainColors.primaryMedium};
  ${_XL_Medium}
`

export const WrapperMonths = styled.div`
  display: flex;
  padding-bottom: 4px;
  gap: var(--month-gap);
  & > div {
    display: flex;
    flex-direction: column;
    flex: 1;
  }
`

export const MonthName = styled.div`
  height: 18px;
  line-height: 1;
  color: ${mainColors.neutralExtremelyDark};
  ${_XS_Medium}
`

export const Week = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 11px;
`

export const WeekSummary = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  width: var(--week-summary-width);
  min-width: var(--week-summary-width);
  max-width: var(--week-summary-width);
`

export const WeekCell = styled.div<{ $color?: string }>`
  --size: round(calc(var(--day-size) * 0.6), 1px);

  width: var(--size);
  height: var(--size);
  min-width: var(--size);
  max-width: var(--size);
  min-height: var(--size);
  max-height: var(--size);
  border-radius: var(--size);
  background-color: ${({ $color }) => $color ?? 'transparent'};
  border: ${({ $color }) => ($color ? undefined : `2px solid ${mainColors.neutralMedium}`)};
`

export const DayCell = styled.div<{ $color?: string; $selected?: boolean; disabled?: boolean }>`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  width: 100%;
  height: 100%;
  background-color: ${({ $color }) => $color ?? mainColors.neutralMedium};
  border-radius: 2px;
  ${({ disabled, $selected }) =>
    !disabled &&
    $selected &&
    css`
      border-radius: 3px;
      z-index: 1;
      animation: ${animPulse} 0.5s ease-in-out alternate infinite;
    `}
`

export const Day = styled.div<{ disabled?: boolean }>`
  --size: round(calc(var(--day-size) * 0.9), 1px);

  user-select: none;
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  width: var(--size);
  height: 100%;
  min-width: var(--size);
  min-height: var(--size);
  max-height: 10px !important;
  padding: 1px;
  ${({ disabled }) =>
    !disabled &&
    css`
      cursor: pointer;
      transition: all 0.1s;
      z-index: 0;
      &:active {
        transform: scale(0.8);
        transition: none;
      }
    `}
`

export const DayEmpty = styled(Day)`
  background-color: transparent;
  pointer-events: none;
`

export const SummaryMonthContainer = styled.div`
  padding-bottom: 4px;
`

export const YearSummaryContainer = styled.div``

export const SelectedMonthHighlight = styled.div<{ dayIndex: number; hasWeekCell?: boolean }>`
  --day-index: ${({ dayIndex }) => dayIndex};
  --icon-size: var(--day-size);
  --padding-x: 2px;
  --padding-x-weekcell: ${({ hasWeekCell }) => (hasWeekCell ? 'var(--week-summary-width)' : '0px')};

  margin: auto calc(-1 * var(--padding-x)) 0 calc(-1 * var(--padding-x));
  margin-bottom: -12px;
  width: calc(100% - var(--padding-x-weekcell) + (var(--padding-x) * 2));
  height: 2px;
  position: relative;
  border-radius: 2px;
  background-color: ${mainColors.accentExtraLight};
  display: flex;
  justify-content: flex-start;
  & > svg {
    width: var(--icon-size);
    margin-top: -6px;
    margin-left: calc(
      calc(((calc(100% - var(--padding-x) * 2) - var(--icon-size)) / 6) * var(--day-index)) +
        var(--padding-x)
    );
    color: ${mainColors.accentExtraLight};
    transition: margin 0.2s;
  }
`

export const TooltipContainer = styled.div`
  position: relative;
  padding: 16px;
  background-color: ${mainColors.neutralExtraLight};
  border-radius: 4px;
  border: 1px solid ${mainColors.neutralMedium};
`

export const ButtonCloseIcon = styled(Button.Flat)`
  position: absolute;
  top: 14px;
  right: 14px;
  color: ${mainColors.neutralDark};
  svg {
    width: 20px;
    height: 20px;
  }
`
